import React, { FC, useState, useEffect, useRef, ChangeEvent, DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useDanceStore, getFriendlyErrorMessage } from '../store/useDanceStore';
import { useStorageStore } from '../store/useStorageStore';
import { X, Link, UploadCloud, AlertTriangle, FileAudio, Loader, Play, Pause, Info } from 'lucide-react';
import { DanceState } from '../types';
import { trackEvent } from '../lib/analytics';
import { decodeAudioFile, calculateFileHash } from '../utils/audioAnalyzer';

export const SoundtrackModal: FC = () => {
  const { t } = useTranslation();
  const isOpen = useDanceStore((s: DanceState) => s.isSoundtrackModalOpen);
  const setOpen = useDanceStore((s: DanceState) => s.setSoundtrackModalOpen);
  const loadMusic = useDanceStore((s: DanceState) => s.loadMusic);
  const isPlaying = useDanceStore((s: DanceState) => s.isPlaying);
  const downloadAndValidateMusic = useDanceStore((s: DanceState) => s.downloadAndValidateMusic);
  const soundtrackModalContext = useDanceStore((s: DanceState) => s.soundtrackModalContext);
  const clearSoundtrackModalContext = useDanceStore((s: DanceState) => s.clearSoundtrackModalContext);
  const loadSequence = useDanceStore((s: DanceState) => s.loadSequence);
  const setActiveModule = useDanceStore((s: DanceState) => s.setActiveModule);

  const [urlValue, setUrlValue] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [isUrlLoading, setIsUrlLoading] = useState(false);
  const [urlError, setUrlError] = useState<string | null>(null);

  // Preview-specific local states
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [audioSource, setAudioSource] = useState<'file' | 'url'>('file');
  const [originalUrl, setOriginalUrl] = useState<string | undefined>(undefined);

  const [isPlayingPreview, setIsPlayingPreview] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const isSequenceContext = soundtrackModalContext !== null;

  // Manage preview object URL creation & disposal
  useEffect(() => {
    if (previewFile) {
      const url = URL.createObjectURL(previewFile);
      setPreviewUrl(url);

      return () => {
        URL.revokeObjectURL(url);
        setPreviewUrl(null);
      };
    } else {
      setPreviewUrl(null);
    }
  }, [previewFile]);

  // Manage HTML5 Audio element lifecycle
  useEffect(() => {
    if (previewUrl) {
      const audio = new Audio(previewUrl);
      audio.volume = 0.8;
      audioRef.current = audio;

      const handlePlay = () => setIsPlayingPreview(true);
      const handlePause = () => setIsPlayingPreview(false);
      const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
      const handleLoadedMetadata = () => setDuration(audio.duration || 0);
      const handleEnded = () => {
        setIsPlayingPreview(false);
        audio.currentTime = 0;
        setCurrentTime(0);
      };

      audio.addEventListener('play', handlePlay);
      audio.addEventListener('pause', handlePause);
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      audio.addEventListener('ended', handleEnded);

      return () => {
        audio.pause();
        audio.removeEventListener('play', handlePlay);
        audio.removeEventListener('pause', handlePause);
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audio.removeEventListener('ended', handleEnded);
        audioRef.current = null;
      };
    } else {
      audioRef.current = null;
      setIsPlayingPreview(false);
      setCurrentTime(0);
      setDuration(0);
    }
  }, [previewUrl]);

  // Reset states on modal open/close; pre-fill from context if present
  useEffect(() => {
    if (isOpen) {
      trackEvent('soundtrack_modal_opened', { context: isSequenceContext ? (soundtrackModalContext?.isLocalFile ? 'local_file' : 'url_error') : 'timeline' });
      setUrlValue(soundtrackModalContext?.initialUrl ?? '');
      setUrlError(soundtrackModalContext?.initialError ?? null);
      setIsUrlLoading(false);
      setPreviewFile(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const stopPreviewAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setIsPlayingPreview(false);
  };

  const handleClose = () => {
    if (isUrlLoading) return;
    stopPreviewAudio();
    trackEvent('soundtrack_modal_closed');
    clearSoundtrackModalContext();
    setOpen(false);
    setPreviewFile(null);

    const tutorialProgress = useStorageStore.getState().tutorialProgress;
    if (tutorialProgress.currentModuleId === 'designing-sequences-with-music') {
      const mockBeats = Array.from({ length: 32 }, (_, i) => i * 0.5);
      const mockMeasures = Array.from({ length: 8 }, (_, m) => m * 2.0);
      const mockWaveform: number[] = [];
      for (let i = 0; i < 200; i++) {
        const phase = (i % 6.25) / 6.25;
        const amplitude = Math.exp(-phase * 3.5) * (0.7 + Math.random() * 0.3);
        mockWaveform.push(amplitude);
      }
      useDanceStore.setState({
        audioFile: null,
        audioFileName: 'electro_beat_120bpm.mp3',
        audioUrl: '',
        audioDuration: 16.0,
        beats: mockBeats,
        measures: mockMeasures,
        bpm: 120,
        waveformData: mockWaveform,
        hasMusicLoaded: true,
        audioHash: 'mock_tutorial_hash_120bpm',
        frameMarkers: {},
        isAnalyzing: false,
        duration: 16,
        metronomeInterval: 0.5,
        keyframes: [],
        currentTime: 0,
        isPlaying: false,
        history: [],
        hasUnsavedChanges: false,
        hasUnsavedSequenceChanges: true,
        actingSource: { type: 'timeline', time: 0 },
      });

      // Synchronously advance step before elements are unmounted
      useStorageStore.getState().updateTutorialProgress({
        currentStepIndex: tutorialProgress.currentStepIndex + 1
      });
    }
  };

  const handleLoadWithoutMusic = () => {
    if (!isSequenceContext) return;
    stopPreviewAudio();
    trackEvent('sequence_loaded_without_music', { sequence_name: soundtrackModalContext!.sequence.name });
    loadSequence(soundtrackModalContext!.sequence);
    setActiveModule('PoseDesigner');
    clearSoundtrackModalContext();
    setOpen(false);
    setPreviewFile(null);
  };

  const proceedWithFile = async (file: File, source: 'file' | 'url', sourceUrl?: string) => {
    const doLoad = async () => {
      try {
        trackEvent('soundtrack_modal_source_selected', { source, source_name: file.name, context: isSequenceContext ? 'sequence' : 'timeline' });
        // In sequence context skip keep/clear dialog — the sequence load will own the timeline
        await loadMusic(file, false, source, sourceUrl);
        if (isSequenceContext) {
          loadSequence(soundtrackModalContext!.sequence);
          setActiveModule('PoseDesigner');
          clearSoundtrackModalContext();
        }
      } catch {
        useDanceStore.getState().confirmAction({
          title: t('music.analysis_failed_title'),
          message: t('music.analysis_failed_message'),
          isAlert: true,
          onConfirm: () => {},
        });
      }
    };

    if (!isSequenceContext && useDanceStore.getState().keyframes.length > 0) {
      useDanceStore.getState().confirmAction({
        title: t('music.load_confirm_title'),
        message: t('music.load_confirm_options_message'),
        showOptions: true,
        options: [
          { key: 'keep', label: t('music.load_option_keep_label'), desc: t('music.load_option_keep_desc') },
          { key: 'clear', label: t('music.load_option_clear_label'), desc: t('music.load_option_clear_desc') },
        ],
        defaultOptionKey: 'keep',
        onConfirm: (selectedKey) => loadMusic(file, selectedKey === 'clear', source, sourceUrl).catch(() => {
          useDanceStore.getState().confirmAction({
            title: t('music.analysis_failed_title'),
            message: t('music.analysis_failed_message'),
            isAlert: true,
            onConfirm: () => {},
          });
        }),
      });
    } else {
      await doLoad();
    }
  };

  const validateLocalFile = async (file: File): Promise<boolean> => {
    // In sequence context, verify hash matches the sequence's required audio
    if (isSequenceContext && soundtrackModalContext!.sequence.audioHash) {
      const fileHash = await calculateFileHash(file);
      if (fileHash !== soundtrackModalContext!.sequence.audioHash) {
        setUrlError(t('library.incorrect_music_message'));
        return false;
      }
    }
    return true;
  };

  const handleFileSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    stopPreviewAudio();
    setIsUrlLoading(true);
    setUrlError(null);
    try {
      await decodeAudioFile(file);
      if (!await validateLocalFile(file)) {
        setIsUrlLoading(false);
        return;
      }
      setPreviewFile(file);
      setAudioSource('file');
      setOriginalUrl(undefined);
    } catch (err) {
      console.error('Invalid local file format:', err);
      setUrlError(t('music.url_fetch_failed_message_invalid_format'));
    } finally {
      setIsUrlLoading(false);
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDrop = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    stopPreviewAudio();
    setIsUrlLoading(true);
    setUrlError(null);
    try {
      await decodeAudioFile(file);
      if (!await validateLocalFile(file)) {
        setIsUrlLoading(false);
        return;
      }
      setPreviewFile(file);
      setAudioSource('file');
      setOriginalUrl(undefined);
    } catch (err) {
      console.error('Invalid dropped file format:', err);
      setUrlError(t('music.url_fetch_failed_message_invalid_format'));
    } finally {
      setIsUrlLoading(false);
    }
  };

  const handleUrlLoad = async () => {
    const url = urlValue.trim();
    if (!url) return;

    stopPreviewAudio();
    setIsUrlLoading(true);
    setUrlError(null);

    try {
      const file = await downloadAndValidateMusic(url);
      setIsUrlLoading(false);
      setPreviewFile(file);
      setAudioSource('url');
      setOriginalUrl(url);
    } catch (err: any) {
      console.error('[url-load-error]:', err);
      setIsUrlLoading(false);
      setUrlError(getFriendlyErrorMessage(err, t));
    }
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlayingPreview) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(err => console.error('Preview playback failed:', err));
    }
  };

  const handleSeek = (newTime: number) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const formatTime = (time: number) => {
    if (isNaN(time) || !isFinite(time)) return '0:00';
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const handleConfirmLoad = async () => {
    if (!previewFile) return;
    stopPreviewAudio();
    setOpen(false);
    await proceedWithFile(previewFile, audioSource, originalUrl);
    setPreviewFile(null);
  };

  return (
    <div className="soundtrack-modal-overlay" onClick={handleClose}>
      <div className="glass-panel soundtrack-modal-content" onClick={e => e.stopPropagation()}>
        <div className="flex-between soundtrack-modal-header">
          <div className="soundtrack-modal-header-left">
            <FileAudio size={16} color="#38bdf8" />
            <h2 className="soundtrack-modal-title">
              {previewFile ? t('music.preview_title') : t('music.modal_title')}
            </h2>
          </div>
          <button onClick={handleClose} className="soundtrack-modal-close-btn" disabled={isUrlLoading}>
            <X size={16} />
          </button>
        </div>

        {isSequenceContext && soundtrackModalContext!.isLocalFile ? (
          <div className="soundtrack-modal-context-notice local-file">
            <Info size={14} style={{ flexShrink: 0, marginTop: '1px' }} />
            <span>{t('music.sequence_local_file_context', { name: soundtrackModalContext!.sequence.audioFileName })}</span>
          </div>
        ) : (
          <p className="soundtrack-modal-subtitle">
            {previewFile ? t('music.preview_subtitle') : t('music.modal_subtitle')}
          </p>
        )}

        <div className="soundtrack-modal-body">
          <div className="soundtrack-modal-left">
            <div className="soundtrack-url-section-header">
              <span>
                {t('music.url_section_header')}{' '}
                <a
                  href="/sharedsoundtracks"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="soundtrack-divider-instructions-link"
                >
                  ({t('music.shared_soundtracks_instructions_label')})
                </a>
              </span>
            </div>

            <div id="soundtrack-modal-url-row" className="soundtrack-modal-url-row">
              <div className="soundtrack-url-input-container">
                <Link size={14} className="url-link-icon" />
                <textarea
                  placeholder={t('music.url_placeholder')}
                  value={urlValue}
                  onChange={e => {
                    setUrlValue(e.target.value);
                    if (audioSource === 'url') {
                      stopPreviewAudio();
                      setPreviewFile(null);
                      setOriginalUrl(undefined);
                    }
                  }}
                  onKeyDown={e => e.key === 'Enter' && !e.shiftKey && !isUrlLoading && (e.preventDefault(), handleUrlLoad())}
                  className="soundtrack-url-input"
                  disabled={isUrlLoading}
                  rows={2}
                />
              </div>
              <button
                onClick={handleUrlLoad}
                disabled={!urlValue.trim() || isUrlLoading}
                className="timeline-btn purple soundtrack-url-btn"
                style={{ minWidth: '70px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                {isUrlLoading ? (
                  <Loader size={14} className="soundtrack-btn-spinner" />
                ) : (
                  t('music.url_load_btn')
                )}
              </button>
              {urlError && (
                <div className="soundtrack-modal-error-overlay">
                  <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: '1px' }} />
                  <span style={{ flex: 1 }}>{urlError}</span>
                  <button
                    className="soundtrack-error-close-btn"
                    onClick={e => { e.stopPropagation(); setUrlError(null); }}
                    type="button"
                  >
                    <X size={12} />
                  </button>
                </div>
              )}
            </div>

            <div className="soundtrack-modal-divider">
              <span>{t('common.or', { defaultValue: 'OR' })}</span>
            </div>

            <div
              id="soundtrack-modal-drop-zone"
              className={`soundtrack-drop-zone ${dragOver ? 'drag-over' : ''}`}
              onDragOver={isUrlLoading ? undefined : handleDragOver}
              onDragLeave={isUrlLoading ? undefined : handleDragLeave}
              onDrop={isUrlLoading ? undefined : handleDrop}
              onClick={isUrlLoading ? undefined : () => fileInputRef.current?.click()}
              style={{ position: 'relative', cursor: isUrlLoading ? 'not-allowed' : undefined }}
            >
              <UploadCloud size={24} className="upload-icon" />
              <p>{t('music.drag_drop_text')}</p>
              <span className="soundtrack-drop-zone-warning">{t('music.drag_drop_warning')}</span>
              {isUrlLoading && (
                <div className="soundtrack-drop-zone-validating-overlay">
                  <Loader size={20} className="soundtrack-btn-spinner" />
                  <span>{t('music.validating_text')}</span>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".mp3,.wav,.ogg,.m4a,.aac,.flac"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                disabled={isPlaying || isUrlLoading}
              />
            </div>

            {/* Audio Preview Player — always visible, disabled until a file is loaded */}
            <div className={`soundtrack-preview-container${!previewFile ? ' soundtrack-preview-disabled' : ''}`}>
              <div className="soundtrack-preview-info">
                <div className="soundtrack-preview-header-row">
                  <FileAudio size={24} color={previewFile ? '#38bdf8' : '#334155'} style={{ flexShrink: 0 }} />
                  <div className="soundtrack-preview-details">
                    <span className="soundtrack-preview-filename">
                      {previewFile ? previewFile.name : t('music.preview_no_file', { defaultValue: 'No file selected' })}
                    </span>
                    {previewFile && (
                      <span className="soundtrack-preview-size">
                        {(previewFile.size / (1024 * 1024)).toFixed(2)} MB •{' '}
                        <span className={`soundtrack-source-badge ${audioSource}`}>
                          {audioSource === 'url' ? 'Cloud URL' : t('music.load_from_file')}
                        </span>
                      </span>
                    )}
                  </div>
                  <div className={`soundtrack-visualizer ${isPlayingPreview ? 'playing' : ''}`}>
                    <div className="soundtrack-visualizer-bar" />
                    <div className="soundtrack-visualizer-bar" />
                    <div className="soundtrack-visualizer-bar" />
                    <div className="soundtrack-visualizer-bar" />
                    <div className="soundtrack-visualizer-bar" />
                    <div className="soundtrack-visualizer-bar" />
                  </div>
                </div>

                <div className="soundtrack-player-controls-row">
                  <button className="soundtrack-play-btn" onClick={togglePlay} type="button" disabled={!previewFile}>
                    {isPlayingPreview ? (
                      <Pause size={14} stroke="#cbd5e1" fill="#cbd5e1" />
                    ) : (
                      <Play size={14} stroke="#cbd5e1" fill="#cbd5e1" style={{ marginLeft: '1.5px' }} />
                    )}
                  </button>

                  <span className="soundtrack-player-time">{formatTime(currentTime)}</span>

                  <input
                    type="range"
                    min={0}
                    max={duration || 100}
                    step={0.1}
                    value={currentTime}
                    onChange={e => handleSeek(parseFloat(e.target.value))}
                    className="preview-seek-slider"
                    disabled={!previewFile}
                  />

                  <span className="soundtrack-player-time">{formatTime(duration)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="soundtrack-modal-footer">
          {isSequenceContext && (
            <button
              onClick={handleLoadWithoutMusic}
              className="confirm-dialog-btn-cancel"
              disabled={isUrlLoading}
              style={{ marginRight: 'auto' }}
            >
              {t('music.load_without_music')}
            </button>
          )}
          <button
            id="soundtrack-modal-cancel-btn"
            onClick={handleClose}
            className="confirm-dialog-btn-cancel"
            disabled={isUrlLoading}
            style={{ marginRight: '8px' }}
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleConfirmLoad}
            disabled={!previewFile || isUrlLoading}
            className="primary confirm-dialog-btn-primary"
          >
            {t('music.load_confirm_btn')}
          </button>
        </div>
      </div>
    </div>
  );
};
